import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import MainApp from './components/MainApp';
import './App.css';

// Protected Route komponenti
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  // Kullanıcı bilgisi localStorage'dan alındıysa, yükleme devam etse bile içeriği göster
  if (user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  return <Navigate to="/login" replace />;
}

// Auth Route komponenti (giriş yapmış kullanıcıları ana sayfaya yönlendir)
function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  
  // Kullanıcı bilgisi localStorage'dan alındıysa, yükleme devam etse bile ana sayfaya yönlendir
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  return children;
}

// Setup kontrolü ile route belirleme
function SetupRoute() {
  const { user, loading, setupChecked, setupData } = useAuth();

  // Kullanıcı bilgisi localStorage'dan alındıysa ve yükleme devam ediyorsa
  // doğrudan dashboard'a yönlendir
  if (user && loading) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading || !setupChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Kullanıcı giriş yapmışsa ana uygulamaya yönlendir
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Setup kontrolü
  if (setupData) {
    // Hiç kullanıcı yoksa register sayfasına yönlendir
    if (!setupData.hasUsers) {
      return <Navigate to="/register" replace />;
    }
    
    // Kullanıcı var ama register kapalıysa login sayfası
    if (setupData.hasUsers && !setupData.registerEnabled) {
      return <Navigate to="/login" replace />;
    }
    
    // Register açıksa login sayfasına yönlendir (kullanıcı isterse register'a gidebilir)
    return <Navigate to="/login" replace />;
  }

  // Varsayılan olarak login sayfası
  return <Navigate to="/login" replace />;
}

// Ana App komponenti
function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            {/* Ana sayfa - setup kontrolü */}
            <Route path="/" element={<SetupRoute />} />
            
            {/* Auth routes */}
            <Route 
              path="/login" 
              element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <AuthRoute>
                  <RegisterPage />
                </AuthRoute>
              } 
            />
            <Route
              path="/forgot-password"
              element={
                <AuthRoute>
                  <ForgotPasswordPage />
                </AuthRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <AuthRoute>
                  <ResetPasswordPage />
                </AuthRoute>
              }
            />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
